import { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "../utils/cn";

interface InlineSelectProps {
  value: string | null;
  options: { id: string; label: string }[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  allowClear?: boolean;
  className?: string;
  displayClassName?: string;
}

export const InlineSelect = ({
  value,
  options,
  onChange,
  placeholder = "Select...",
  allowClear = false,
  className = "",
  displayClassName = "",
}: InlineSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Display Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors text-left",
          !selectedOption && "text-gray-400",
          displayClassName
        )}
      >
        <span className="text-sm truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {selectedOption && allowClear && (
          <X
            className="w-3 h-3 hover:text-red-600 flex-shrink-0"
            onClick={handleClear}
          />
        )}
        {!allowClear && (
          <ChevronDown className="w-3 h-3 flex-shrink-0" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="fixed z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-64 overflow-hidden flex flex-col"
          style={{
            top: containerRef.current?.getBoundingClientRect().bottom,
            left: containerRef.current?.getBoundingClientRect().left,
          }}
        >
          {/* Search Input */}
          <div className="px-2 pb-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-48 flex flex-col">
            {allowClear && selectedOption && (
              <button
                onClick={handleClear}
                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100"
              >
                <X className="w-4 h-4" />
                <span>Clear selection</span>
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors block",
                    option.id === value &&
                      "bg-primary-50 text-primary-700 font-medium"
                  )}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
